import React from "react";

import { ACCENT, FX, inr } from "./ui";

const ChevR = ({ size = 10 }) => (
  <svg width={size * 0.6} height={size} viewBox="0 0 6 10" fill="none">
    <path
      d="M1 1l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function SectionHeader({ onViewAll }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        margin: "4px 4px 11px",
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FX.muted3,
        }}
      >
        Pending action
      </div>
      <button
        type="button"
        onClick={onViewAll}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          gap: 5,
          color: FX.teal,
          fontSize: 12.5,
          fontWeight: 700,
          padding: 2,
          whiteSpace: "nowrap",
        }}
      >
        View signed statements <ChevR />
      </button>
    </div>
  );
}

function PendingLedgerCard({ request, onReview }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        border: `1px solid ${FX.line}`,
        boxShadow:
          "0 1px 2px rgba(16,32,30,0.04), 0 8px 24px rgba(16,32,30,0.06)",
        padding: "14px 16px 15px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            position: "relative",
            width: 40,
            height: 40,
            borderRadius: 12,
            background: FX.mint,
            color: FX.teal,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg width={17} height={19} viewBox="0 0 17 19" fill="none">
            <path
              d="M2 1.5h8L15 6v11.5H2z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path
              d="M10 1.5V6h5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
          <span
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 11,
              height: 11,
              borderRadius: "50%",
              background: "#FFB020",
              border: "2px solid #fff",
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              color: FX.ink,
              letterSpacing: "-0.01em",
            }}
          >
            Statement of Accounts
          </div>
          <div style={{ fontSize: 11, color: FX.muted3, marginTop: 1 }}>
            Awaiting your signature
          </div>
        </div>
        <button
          type="button"
          onClick={onReview}
          style={{
            background: ACCENT,
            color: "#fff",
            border: "none",
            borderRadius: 999,
            padding: "9px 16px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
            boxShadow: "0 5px 14px rgba(255,106,0,0.30)",
          }}
        >
          E-Sign <ChevR />
        </button>
      </div>
      <div
        style={{ borderTop: `1.5px dashed ${FX.line}`, margin: "13px 0 11px" }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <Row label="Statement period" value={request.period} />
        <Row label="Balance as per ledger" value={inr(request.ledger_outstanding)} />
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    <span
      style={{
        fontSize: 12,
        color: FX.muted2,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: FX.ink,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  </div>
);

function AllCaughtUp() {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        border: `1px solid ${FX.line}`,
        boxShadow: "0 1px 2px rgba(16,32,30,0.04)",
        padding: "15px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: "rgba(1,136,104,0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: FX.green,
          flexShrink: 0,
        }}
      >
        <svg width={15} height={15} viewBox="0 0 15 15" fill="none">
          <path
            d="M2 8l4 4 7-9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: FX.ink }}>
          All caught up
        </div>
        <div style={{ fontSize: 11.5, color: FX.muted, marginTop: 1 }}>
          No statements awaiting signature
        </div>
      </div>
    </div>
  );
}

// Full "Pending action" section: header + (pending card | empty state).
// Renders nothing when there's neither a pending request nor history to link to.
export default function PendingActionSection({
  request,
  hasHistory,
  onReview,
  onViewSignedList,
}) {
  if (!request && !hasHistory) return null;
  return (
    <div style={{ marginTop: 2 }}>
      <SectionHeader onViewAll={onViewSignedList} />
      {request ? (
        <PendingLedgerCard request={request} onReview={onReview} />
      ) : (
        <AllCaughtUp />
      )}
    </div>
  );
}
